<template>
  <div class="form-group">
    <h3>Step {{ currentStep }}. Your traveler details</h3>
    <div class="infomsg">Please ensure the information below is exactly as recorded in your <u>passport</u>. Any discrepancies can lead to the airline denying boarding on departure.</div>

    <div class="form-row">
      <input v-model="formData.passportFirstName" type="text" maxlength="25" required placeholder="First name" />
    </div>

    <div class="form-row">
      <input v-model="formData.passportMiddleName" type="text" maxlength="25" placeholder="Middle name/s" />
    </div>

    <div class="form-row">
      <input v-model="formData.passportLastName" type="text" maxlength="25" required placeholder="Last name" />
    </div>

    <div class="form-row">
      <select v-model="formData.passportCountry" required>
        <option value="" disabled>Passport Country</option>
        <option :value="231">United States</option>
        <option v-for="country in countries" :key="country.id" :value="country.id">
          {{ country.title }}
        </option>
      </select>
    </div>

    <div class="form-row">
      <div class="form-row form-col">
        <div class="form-col">
          <select v-model="formData.dateOfBirthYear" class="year" required>
            <option value="" disabled>DOB year</option>
            <option v-for="year in years" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
        </div>
        <div class="form-col">
          <select v-model="formData.dateOfBirthMonth" class="month" required>
            <option value="" disabled>DOB month</option>
            <option v-for="n in 12" :key="n" :value="n">
              {{ n < 10 ? "0" + n : n }}
            </option>
          </select>
        </div>
        <div class="form-col">
          <select v-model="formData.dateOfBirthDay" class="day" required>
            <option value="" disabled>DOB date</option>
            <option v-for="day in 31" :key="day" :value="day">
              {{ day }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="form-row">
      <div class="form-col">
        <select v-model="formData.sex" required>
          <option value="" disabled>Gender</option>
          <option value="f">Female</option>
          <option value="m">Male</option>
        </select>
      </div>
      <div class="form-col">
        <input v-model="formData.phoneNumber" type="text" maxlength="255" placeholder="Phone number (support on travel day)" @focus="addCountryCode" @input="filterPhoneInput" />
      </div>
    </div>

    <div class="form-row">
      <div class="form-col">
        <select v-model="formData.seatPreference" required>
          <option value="" disabled>Seat Preference</option>
          <option :value="null">No Preference</option>
          <option :value="false">Aisle</option>
          <option :value="true">Window</option>
        </select>
      </div>
      <div class="form-col">
        <input v-model="formData.dietaryRequirements" type="text" maxlength="255" placeholder="Dietary requirements (optional)" />
      </div>
    </div>

    <div v-if="isUM" class="form-group">
      <h4>Unaccompanied Minor Information</h4>
      <div class="infomsg">Your date of birth classifies you as an <u>unaccompanied minor</u>, so we must request your parent/guardian information. We will register this information with the airline.</div>
      <div class="form-row">
        <div class="form-col">
          <input v-model="formData.umName" type="text" maxlength="255" placeholder="Guardian full name" />
        </div>
        <div class="form-col">
          <input v-model="formData.umPhone" type="text" maxlength="255" placeholder="Guardian phone number" />
        </div>
      </div>
      <div class="form-row">
        <input v-model="formData.umAddress" type="text" maxlength="255" placeholder="Guardian address (street, city, state)" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from "vue";
import useApi from "../utils/apiCalls.js";

defineProps({
  config: {
    type: Object,
    required: true,
  },
  currentStep: {
    type: Number,
    required: true,
  },
});

// Get shared state from parent
const { formData } = inject("landerState");

const { apiGetRequest } = useApi();
const countries = ref([]);

const years = computed(() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i >= currentYear - 100; i--) {
    years.push(i);
  }
  return years;
});

const isUM = computed(() => {
  if (!formData.dateOfBirthYear || !formData.dateOfBirthMonth || !formData.dateOfBirthDay) {
    return false;
  }
  const dob = new Date(formData.dateOfBirthYear, formData.dateOfBirthMonth - 1, formData.dateOfBirthDay);
  const limit = new Date();
  limit.setFullYear(limit.getFullYear() - 15);
  return dob > limit;
});

const addCountryCode = () => {
  if (!formData.phoneNumber.startsWith("+1")) {
    formData.phoneNumber = "+1" + formData.phoneNumber;
  }
};

const filterPhoneInput = () => {
  formData.phoneNumber = formData.phoneNumber.replace(/[^+\d]/g, "");
};

onMounted(async () => {
  try {
    countries.value = (await apiGetRequest({ path: "/countries" })) || [];
  } catch {
    // Error loading countries - silently fail
  }
});
</script>
